// The ops catalog's words and rules, kept out of the components: what each link
// state is called, why a product can't be switched on, what the strip counts, and
// how a OneBill plan reads. Pure functions over the records the server returns.
import { codeSourceOf, formatCents, type AutotaskDifference, type AutotaskSyncState, type Billing, type CatalogCodeReport, type CatalogProductV2, type CatalogSyncState, type Cents, type OneBillMapping } from "@phoneware/lens-domain";
import type { ChipKind } from "../../format.js";
import type { IconName } from "../../icons.js";

export type Product = CatalogProductV2;
export type OneBillRow = CatalogSyncState["notInOmni"]["onebill"][number];
export type AutotaskRow = CatalogSyncState["notInOmni"]["autotask"][number];
export type OneBillPlan = OneBillRow["plans"][number];

export type FilterId = "quotable" | "needs" | "create" | "notq" | "changed" | "notat";

const PERIOD_WORD: Record<string, string> = { quarterly: "quarterly", semi_annual: "semi-annually", yearly: "yearly" };

/** "Product" or "Service": what the item is, or will be, in Autotask. */
export function kindWord(p: Pick<Product, "autotask" | "billing">): "Product" | "Service" {
  const kind = p.autotask?.kind ?? (p.billing === "monthly" ? "service" : "product");
  return kind === "service" ? "Service" : "Product";
}

/** "Autotask Product 29683443", or the kind it will be created as. */
export function atRef(p: Pick<Product, "autotask" | "billing">): string {
  return p.autotask ? `Autotask ${kindWord(p)} ${p.autotask.id}` : `an Autotask ${kindWord(p)}`;
}

/** The billing Omni quotes, or why it can't: "Bills yearly in Autotask". */
export function billedText(p: Pick<Product, "billing" | "autotaskSync">): string {
  if (p.billing === "monthly") return "Monthly";
  if (p.billing === "one_time") return "One-time";
  const word = p.autotaskSync.period ? PERIOD_WORD[p.autotaskSync.period] : undefined;
  return word ? `Bills ${word} in Autotask` : "No billing period in Autotask";
}

/**
 * Every reason Omni refuses to switch a product on for dealers, in words, in the
 * order the mock lists them. Empty when dealers can quote it. The server applies
 * the same rules (canEnable) and refuses with the first one.
 */
export function blockers(p: Product): string[] {
  const reasons: string[] = [];
  if (!p.billing) reasons.push(billedText(p));
  if (!p.active) reasons.push("Inactive in Autotask");
  const st = p.autotaskSync.state;
  if (st === "creating" || st === "push_failed" || !p.autotask) reasons.push("Not in Autotask yet");
  if (st === "missing_in_autotask") reasons.push("Missing in Autotask");
  if (st === "changed_in_autotask" && p.autotaskSync.differences.some((d) => d.field === "active")) reasons.push("Changed in Autotask");
  if (p.billingLink.state === "unreviewed" || p.billingLink.state === "create") reasons.push("Not linked to OneBill yet");
  if (p.billingLink.state === "not_billed") reasons.push("Not quoted in Omni");
  return reasons;
}

export function quotable(p: Product): boolean {
  return blockers(p).length === 0;
}

export interface StripCounts {
  quotable: number;
  offLinked: number;
  needs: number;
  create: number;
  notq: number;
  changed: number;
  edited: number;
  missing: number;
  notat: number;
  creating: number;
  failed: number;
}

export function stripCounts(list: Product[]): StripCounts {
  const c: StripCounts = { quotable: 0, offLinked: 0, needs: 0, create: 0, notq: 0, changed: 0, edited: 0, missing: 0, notat: 0, creating: 0, failed: 0 };
  for (const p of list) {
    const link = p.billingLink.state;
    if (p.active) {
      if (link === "linked" && p.enabled && quotable(p)) c.quotable += 1;
      else if (link === "linked" && !p.enabled) c.offLinked += 1;
      if (link === "unreviewed") c.needs += 1;
      if (link === "create") c.create += 1;
      if (link === "not_billed") c.notq += 1;
    }
    const st = p.autotaskSync.state;
    if (st === "changed_in_autotask") {
      c.changed += 1;
      c.edited += 1;
    }
    if (st === "missing_in_autotask") {
      c.changed += 1;
      c.missing += 1;
    }
    if (st === "creating") {
      c.notat += 1;
      c.creating += 1;
    }
    if (st === "push_failed") {
      c.notat += 1;
      c.failed += 1;
    }
  }
  return c;
}

export const FILTERS: Record<FilterId, (p: Product) => boolean> = {
  quotable: (p) => p.active && p.billingLink.state === "linked" && p.enabled && quotable(p),
  needs: (p) => p.active && p.billingLink.state === "unreviewed",
  create: (p) => p.active && p.billingLink.state === "create",
  notq: (p) => p.active && p.billingLink.state === "not_billed",
  changed: (p) => p.autotaskSync.state === "changed_in_autotask" || p.autotaskSync.state === "missing_in_autotask",
  notat: (p) => p.autotaskSync.state === "creating" || p.autotaskSync.state === "push_failed",
};

export const FILTER_NAME: Record<FilterId, string> = {
  quotable: "quotable",
  needs: "needing a OneBill link",
  create: "to create in OneBill",
  notq: "not quoted in Omni",
  changed: "changed in Autotask",
  notat: "not in Autotask yet",
};

export const FILTER_EMPTY: Record<FilterId, string> = {
  quotable: "Nothing is quotable yet. A product needs a OneBill link, an Autotask record in sync, and Dealers see switched on.",
  needs: "Nothing needs a OneBill link.",
  create: "Nothing is waiting to be created in OneBill. In the review queue, choose Create in OneBill when OneBill has no match.",
  notq: "Nothing is marked Not quoted in Omni.",
  changed: "Nothing was edited in Autotask outside Omni since the last check.",
  notat: "Everything Omni added is in Autotask.",
};

export interface Look {
  label: string;
  kind: ChipKind;
  icon: IconName | null;
  spin?: boolean;
}

export const AT_LOOK: Record<AutotaskSyncState, Look> = {
  in_sync: { label: "In sync", kind: "ok", icon: "check" },
  creating: { label: "Creating", kind: "", icon: "refresh-cw", spin: true },
  push_failed: { label: "Push failed", kind: "stop", icon: "triangle-alert" },
  changed_in_autotask: { label: "Changed in Autotask", kind: "warn", icon: "arrow-right-left" },
  missing_in_autotask: { label: "Missing in Autotask", kind: "stop", icon: "ban" },
};

/**
 * Why a check linked a product on its own (#2057): "Same code EDGBAS" when OneBill
 * has one product with its code, else "Same name" (also for links made before codes).
 */
export function autoReason(p: Pick<Product, "billingLink" | "code">): Look {
  return p.billingLink.tier === "code" && p.code.trim() ? { label: `Same code ${p.code.trim()}`, kind: "ok", icon: "check" } : { label: "Same name", kind: "quiet", icon: null };
}

export function linkLook(p: Pick<Product, "billingLink" | "code">): Look {
  switch (p.billingLink.state) {
    case "linked":
      return p.billingLink.via === "auto" ? autoReason(p) : { label: "Linked", kind: "ok", icon: "link" };
    case "unreviewed":
      return { label: "Needs OneBill link", kind: "warn", icon: "triangle-alert" };
    case "create":
      // #2156: a create that failed, or one marked before Omni asked what to copy, needs a person.
      if (p.billingLink.createError) return { label: "Create failed", kind: "stop", icon: "triangle-alert" };
      if (!p.billingLink.copyFrom) return { label: "Needs setup to copy", kind: "warn", icon: "triangle-alert" };
      return { label: "To create in OneBill", kind: "", icon: "plus" };
    case "not_billed":
      return { label: "Not quoted in Omni", kind: "quiet", icon: "ban" };
  }
}

/** " a month", " once", or nothing for a billing Omni can't quote: what follows a price. */
export function perText(billing: Billing | null): string {
  return billing === "monthly" ? " a month" : billing === "one_time" ? " once" : "";
}

/** What the toast says once ops choose what a new OneBill product copies (#2156). */
export function copyNote(name: string, from: OneBillMapping): string {
  return `The next sync creates ${name} in OneBill, copying ${from.productCode} plan ${from.pricePlanId}.`;
}

/**
 * A create that failed because OneBill's product differs from the one it copied (#2156): the
 * sentence and each field, as the server's adapter words it
 * (apps/lens/server/src/adapters/onebill-catalog.ts): "OneBill created X but it differs from Y:
 * <field> expected <a> got <b>; ... Omni did not link it." or "OneBill already has X and it
 * differs from Y: ... Fix or delete it in OneBill, then apply again."
 */
export interface CreateDiff {
  head: string;
  /** True when the apply made the product; false when OneBill already had it. */
  created: boolean;
  /** The OneBill product whose setup was copied. */
  template: string;
  tail: string;
  rows: Array<{ field: string; expected: string; got: string }>;
}

const DIFFERS = /^(OneBill (?:created (\S+) but it|already has (\S+) and it) differs from (\S+)): (.+)\. (Omni did not link it\.|Fix or delete it in OneBill, then apply again\.)$/s;

/** The fields of a "differs" failure, or null for any other message, which is shown as it is. */
export function createDiff(message: string): CreateDiff | null {
  const m = DIFFERS.exec(message);
  if (!m) return null;
  const rows: CreateDiff["rows"] = [];
  for (const part of m[5].split("; ")) {
    const field = /^(.+?) expected (.+?) got (.+)$/s.exec(part);
    // A feature the copy lost: "feature expected RPP Account ID not found in created product".
    const lost = /^(.+?) expected (.+) not found in created product$/s.exec(part);
    if (field) rows.push({ field: field[1], expected: field[2], got: field[3] });
    else if (lost) rows.push({ field: lost[1], expected: lost[2], got: "Missing" });
    else return null;
  }
  return { head: m[1], created: m[2] !== undefined, template: m[4], tail: m[6], rows };
}

/** Services first, "No category" last, the rest by name. */
export function categoryOrder(a: string, b: string): number {
  const rank = (c: string) => (c === "Services" ? 0 : c === "No category" ? 2 : 1);
  return rank(a) - rank(b) || a.localeCompare(b);
}

export function categoriesOf(list: ReadonlyArray<{ category: string }>): string[] {
  return [...new Set(list.map((p) => p.category))].sort(categoryOrder);
}

/** A OneBill plan's money, the way the mock reads it: "$5.00 a month", "$149.00 once". */
export function planText(plan: { billing: Billing | "other"; amount: Cents | null }): string {
  if (plan.amount === null) return "No charge set";
  if (plan.billing === "monthly") return `${formatCents(plan.amount)} a month`;
  if (plan.billing === "one_time") return `${formatCents(plan.amount)} once`;
  return `${formatCents(plan.amount)}, billed another way`;
}

/** The plans an Omni product could bill on: active, monthly or one-time, with an amount. */
export function usablePlans(row: Pick<OneBillRow, "plans">): Array<OneBillPlan & { billing: Billing; amount: Cents }> {
  return row.plans.filter((pl): pl is OneBillPlan & { billing: Billing; amount: Cents } => pl.active && pl.billing !== "other" && pl.amount !== null);
}

/** OneBill names carry "-PHNW" or "-Retail"; Omni's names don't. */
export function withoutSuffix(text: string): string {
  return text.replace(/\s*-\s*(phnw|retail)\b.*$/i, "").trim();
}

export function pct(score: number): string {
  return `${Math.round(score * 100)}%`;
}

export function scoreClass(score: number): string {
  return score >= 0.75 ? "score hi" : score >= 0.4 ? "score mid" : "score";
}

export const FIELD_LABEL: Record<AutotaskDifference["field"], string> = {
  name: "Name",
  description: "Description",
  listPrice: "List price",
  unitCost: "Unit cost",
  active: "Active",
  category: "Category",
  billing: "Billed",
};

/** A difference's value as a person reads it: money in dollars, Active in words, empty as "None". */
export function fieldValue(d: AutotaskDifference, value: string): string {
  if (d.field === "listPrice" || d.field === "unitCost") return value === "" ? "Not set" : formatCents(Number(value));
  if (d.field === "active") return value === "true" ? "Active" : "Inactive";
  if (d.field === "billing") return value === "monthly" ? "Monthly" : value === "one_time" ? "One-time" : value || "Not set";
  return value === "" ? "None" : value;
}

/** "2:30 PM": the time of day, for the check card and the Autotask states. */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** The scheduled check runs every 15 minutes (Cloud Scheduler, every quarter hour UTC); the next one after `now`. */
export function nextScheduledCheck(now: Date): string {
  const slot = 15 * 60 * 1000;
  return new Date(Math.floor(now.getTime() / slot) * slot + slot).toISOString();
}
